import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "../components/icons/CloseIcon";
import { useModalMotion } from "../hooks/useModalMotion";
import { readAuthSession } from "../services/authSession";
import { formatPrice } from "./shopUtils";

function resolveUserEmail(initialEmail = "") {
  const fromProp = String(initialEmail || "").trim();
  if (fromProp) return fromProp;
  const user = readAuthSession()?.user;
  return String(user?.email || user?.Email || "").trim();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {{
 *   show: boolean,
 *   onHide: () => void,
 *   onSubmit: (email: string) => void | Promise<void>,
 *   isSubmitting?: boolean,
 *   initialEmail?: string,
 *   pricing?: {
 *     subtotal: number,
 *     processingFee: number,
 *     salesTax: number,
 *     total: number,
 *     processingFeePercent: number,
 *     salesTaxPercent: number,
 *   } | null,
 * }} props
 */
function GuestEmailModal({
  show,
  onHide,
  onSubmit,
  isSubmitting = false,
  initialEmail = "",
  pricing = null,
}) {
  const defaultEmail = useMemo(
    () => resolveUserEmail(initialEmail),
    [initialEmail]
  );
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setEmail(resolveUserEmail(initialEmail));
      setError("");
    }
  }, [show, initialEmail]);

  const { requestClose, motionClassName } = useModalMotion(onHide, {
    open: show,
  });

  if (!show) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Email is required.");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    await onSubmit(trimmed);
  };

  return (
    <div
      className={`guestEmailModal${motionClassName}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guestEmailModalTitle"
    >
      <button
        type="button"
        className="guestEmailModal__backdrop"
        onClick={isSubmitting ? undefined : requestClose}
        aria-label="Close"
        disabled={isSubmitting}
      />
      <div
        className="guestEmailModal__card"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="guestEmailModal__close"
          onClick={requestClose}
          disabled={isSubmitting}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2 id="guestEmailModalTitle" className="guestEmailModal__title">
          Almost there
        </h2>
        <p className="guestEmailModal__subtitle">
          Enter your email so we can attach this order for pickup at reception.
        </p>

        {pricing ? (
          <div className="guestEmailModal__pricing" aria-live="polite">
            <div className="guestEmailModal__pricingRow">
              <span>Subtotal</span>
              <span>{formatPrice(pricing.subtotal)}</span>
            </div>
            <div className="guestEmailModal__pricingRow">
              <span>Processing fee ({pricing.processingFeePercent}%)</span>
              <span>{formatPrice(pricing.processingFee)}</span>
            </div>
            <div className="guestEmailModal__pricingRow">
              <span>Sales tax ({pricing.salesTaxPercent}%)</span>
              <span>{formatPrice(pricing.salesTax)}</span>
            </div>
            <div className="guestEmailModal__pricingTotal">
              <span>Total</span>
              <strong>{formatPrice(pricing.total)} USD</strong>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="guestEmailModal__form">
          <label className="guestEmailModal__field">
            <span className="guestEmailModal__label">Email</span>
            <input
              className="guestEmailModal__input"
              type="email"
              name="guestEmail"
              placeholder="Email address"
              value={email}
              autoComplete="email"
              readOnly
              tabIndex={-1}
              disabled={isSubmitting}
              onFocus={(event) => event.target.blur()}
            />
          </label>
          {error ? <div className="guestEmailModal__error">{error}</div> : null}

          <button
            type="submit"
            className="guestEmailModal__submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Placing order…"
              : pricing
                ? `Confirm · ${formatPrice(pricing.total)}`
                : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default GuestEmailModal;
